import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import type { Message, ContactInfo, Attachment, ProjectSpec, ConversationMode, SlackThread } from '@bitio-builder/shared';
import * as api from './useApi';

export type ChatStatus =
  | 'idle'
  | 'loading'
  | 'active'
  | 'completing'
  | 'completed'
  | 'error';

interface ChatState {
  status: ChatStatus;
  conversationId: string | null;
  messages: Message[];
  error: string | null;
  showSpecReview: boolean;
  shouldShowContactForm: boolean;
  projectSpec: ProjectSpec | null;
  conversationSummary: string | null;
  isWaitingForResponse: boolean;
  uploadedAttachments: Attachment[];
  mode: ConversationMode | null;
  slackThread: SlackThread | null;
  showLeaveMessage: boolean;
  showNoReplyFallback: boolean;
  hasReceivedSlackReply: boolean;
}

export function useChat(language: 'sv' | 'en', noReplyTimeoutSeconds = 60) {
  const [state, setState] = useState<ChatState>({
    status: 'idle',
    conversationId: null,
    messages: [],
    error: null,
    showSpecReview: false,
    shouldShowContactForm: false,
    projectSpec: null,
    conversationSummary: null,
    isWaitingForResponse: false,
    uploadedAttachments: [],
    mode: null,
    slackThread: null,
    showLeaveMessage: false,
    showNoReplyFallback: false,
    hasReceivedSlackReply: false,
  });

  const pendingAttachmentsRef = useRef<Attachment[]>([]);
  const pollingIntervalRef = useRef<number | null>(null);
  const lastMessageTimestampRef = useRef<string | null>(null);
  const noReplyTimerRef = useRef<number | null>(null);
  const lastUserMessageTimeRef = useRef<number | null>(null);

  const startConversation = useCallback(async (mode: ConversationMode = 'claude', userIntro?: api.UserIntro) => {
    setState((s) => ({ ...s, status: 'loading', error: null, mode }));

    const response = await api.createConversation(undefined, language, mode, userIntro);

    // Check for rate limit before checking success
    if ((response as { rateLimitExceeded?: boolean }).rateLimitExceeded) {
      setState((s) => ({
        ...s,
        status: 'idle',
        showLeaveMessage: true,
        mode: null,
      }));
      return;
    }

    if (response.success && response.data) {
      const initialMessages = response.data!.messages;
      // Track the latest message timestamp for polling
      if (initialMessages.length > 0) {
        lastMessageTimestampRef.current = initialMessages[initialMessages.length - 1].timestamp;
      }

      setState((s) => ({
        ...s,
        status: 'active',
        conversationId: response.data!.id,
        messages: initialMessages,
        slackThread: response.data!.slackThread || null,
      }));
    } else {
      setState((s) => ({
        ...s,
        status: 'error',
        error: response.error?.message || 'Failed to start conversation',
        mode: null,
      }));
    }
  }, [language]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!state.conversationId || state.status !== 'active') return;

      // Add user message optimistically
      const tempUserMessage: Message = {
        id: `temp-${Date.now()}`,
        conversationId: state.conversationId,
        role: 'user',
        content,
        attachments: pendingAttachmentsRef.current.length > 0 ? [...pendingAttachmentsRef.current] : undefined,
        timestamp: new Date().toISOString(),
        source: 'widget',
      };

      setState((s) => ({
        ...s,
        messages: [...s.messages, tempUserMessage],
        isWaitingForResponse: state.mode === 'claude', // Only show waiting for Claude mode
      }));

      const attachmentsToSend = [...pendingAttachmentsRef.current];
      pendingAttachmentsRef.current = [];

      const response = await api.sendMessage(
        state.conversationId,
        content,
        attachmentsToSend.length > 0 ? attachmentsToSend : undefined
      );

      // Check for rate limit - show leave message form
      if ((response as { rateLimitExceeded?: boolean }).rateLimitExceeded) {
        setState((s) => ({
          ...s,
          messages: s.messages.slice(0, -1), // Remove optimistic message
          isWaitingForResponse: false,
          showLeaveMessage: true,
        }));
        return;
      }

      if (response.success && response.data) {
        // In Slack mode, we only get the user message back - responses come from polling
        if (state.mode === 'slack') {
          // Update the timestamp for polling
          lastMessageTimestampRef.current = response.data!.message.timestamp;
          // Track when user sent message for no-reply timer
          lastUserMessageTimeRef.current = Date.now();
          setState((s) => ({
            ...s,
            // Replace temp message with real one
            messages: [...s.messages.slice(0, -1), response.data!.message],
            isWaitingForResponse: false,
            showNoReplyFallback: false, // Reset on new message
          }));
        } else {
          // Claude mode - we get both user message and assistant response
          const isReadyForReview = response.data!.shouldShowContactForm || false;
          setState((s) => ({
            ...s,
            // Replace temp message with real one and add assistant response
            messages: [
              ...s.messages.slice(0, -1),
              response.data!.message,
              response.data!.assistantMessage,
            ],
            // Show spec review when Claude thinks we have enough info
            showSpecReview: isReadyForReview,
            shouldShowContactForm: false, // Don't show contact form directly
            // Merge projectSpec progressively (update with new data, preserve existing)
            projectSpec: response.data!.projectSpec
              ? { ...s.projectSpec, ...response.data!.projectSpec }
              : s.projectSpec,
            conversationSummary: response.data!.conversationSummary || s.conversationSummary,
            isWaitingForResponse: false,
          }));
        }
      } else {
        setState((s) => ({
          ...s,
          error: response.error?.message || 'Failed to send message',
          // Remove optimistic message on error
          messages: s.messages.slice(0, -1),
          isWaitingForResponse: false,
        }));
      }
    },
    [state.conversationId, state.status, state.mode]
  );

  const uploadFile = useCallback(
    async (file: File): Promise<Attachment | null> => {
      if (!state.conversationId) return null;

      const response = await api.uploadFile(state.conversationId, file);

      if (response.success && response.data) {
        const attachment: Attachment = {
          id: response.data.fileId,
          url: response.data.url,
          filename: response.data.filename,
          mimeType: response.data.mimeType,
          size: response.data.size,
        };
        pendingAttachmentsRef.current.push(attachment);
        // Also track in uploadedAttachments for display in ProjectSpec panel
        setState((s) => ({
          ...s,
          uploadedAttachments: [...s.uploadedAttachments, attachment],
        }));
        return attachment;
      }

      setState((s) => ({
        ...s,
        error: response.error?.message || 'Failed to upload file',
      }));
      return null;
    },
    [state.conversationId]
  );

  const removeAttachment = useCallback((attachmentId: string) => {
    // Remove from pending attachments (if not yet sent)
    pendingAttachmentsRef.current = pendingAttachmentsRef.current.filter(
      (a) => a.id !== attachmentId
    );
    // Remove from uploadedAttachments state
    setState((s) => ({
      ...s,
      uploadedAttachments: s.uploadedAttachments.filter((a) => a.id !== attachmentId),
    }));
  }, []);

  const completeConversation = useCallback(
    async (contactInfo: ContactInfo) => {
      if (!state.conversationId) return;

      setState((s) => ({ ...s, status: 'completing' }));

      const response = await api.completeConversation(
        state.conversationId,
        contactInfo
      );

      if (response.success) {
        setState((s) => ({
          ...s,
          status: 'completed',
        }));
      } else {
        setState((s) => ({
          ...s,
          status: 'active',
          error: response.error?.message || 'Failed to complete conversation',
        }));
      }
    },
    [state.conversationId]
  );

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  // User confirms the spec and wants to submit contact info
  const confirmSpec = useCallback((editedSpec: ProjectSpec) => {
    setState((s) => ({
      ...s,
      showSpecReview: false,
      shouldShowContactForm: true,
      projectSpec: editedSpec, // Store the user-edited spec
    }));
  }, []);

  // User wants to continue refining the spec
  const continueRefining = useCallback(() => {
    setState((s) => ({
      ...s,
      showSpecReview: false,
      shouldShowContactForm: false,
    }));
  }, []);

  // User wants to submit the spec early (before Claude suggests it)
  const submitSpecEarly = useCallback(() => {
    setState((s) => ({
      ...s,
      showSpecReview: true,
    }));
  }, []);

  const reset = useCallback(() => {
    // Stop polling if active
    if (pollingIntervalRef.current) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    lastMessageTimestampRef.current = null;

    // Clear no-reply timer
    if (noReplyTimerRef.current) {
      window.clearTimeout(noReplyTimerRef.current);
      noReplyTimerRef.current = null;
    }
    lastUserMessageTimeRef.current = null;

    setState({
      status: 'idle',
      conversationId: null,
      messages: [],
      error: null,
      showSpecReview: false,
      shouldShowContactForm: false,
      projectSpec: null,
      conversationSummary: null,
      isWaitingForResponse: false,
      uploadedAttachments: [],
      mode: null,
      slackThread: null,
      showLeaveMessage: false,
      showNoReplyFallback: false,
      hasReceivedSlackReply: false,
    });
    pendingAttachmentsRef.current = [];
  }, []);

  // Show/hide leave message form
  const showLeaveMessageForm = useCallback(() => {
    setState((s) => ({ ...s, showLeaveMessage: true }));
  }, []);

  const hideLeaveMessageForm = useCallback(() => {
    setState((s) => ({ ...s, showLeaveMessage: false }));
  }, []);

  // No-reply timer effect for Slack mode
  useEffect(() => {
    // Only run timer in Slack mode when user has sent a message
    // and we haven't received any Slack reply yet
    if (state.mode !== 'slack' || !lastUserMessageTimeRef.current || state.hasReceivedSlackReply) {
      return;
    }

    // Clear any existing timer
    if (noReplyTimerRef.current) {
      window.clearTimeout(noReplyTimerRef.current);
    }

    // Set timer to show fallback after timeout
    noReplyTimerRef.current = window.setTimeout(() => {
      setState((s) => ({ ...s, showNoReplyFallback: true }));
    }, noReplyTimeoutSeconds * 1000);

    return () => {
      if (noReplyTimerRef.current) {
        window.clearTimeout(noReplyTimerRef.current);
        noReplyTimerRef.current = null;
      }
    };
  }, [state.mode, state.messages, state.hasReceivedSlackReply, noReplyTimeoutSeconds]);

  // Polling effect for Slack mode
  useEffect(() => {
    // Only poll when in Slack mode with an active conversation
    if (state.mode !== 'slack' || state.status !== 'active' || !state.conversationId) {
      // Stop polling if conditions aren't met
      if (pollingIntervalRef.current) {
        window.clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Start polling every 2 seconds
    pollingIntervalRef.current = window.setInterval(async () => {
      if (!state.conversationId) return;

      const response = await api.getMessages(
        state.conversationId,
        lastMessageTimestampRef.current || undefined
      );

      if (response.success && response.data && response.data.messages.length > 0) {
        const newMessages = response.data.messages;
        // Update the timestamp to the latest message
        lastMessageTimestampRef.current = newMessages[newMessages.length - 1].timestamp;

        setState((s) => {
          // Deduplicate: only add messages that don't already exist
          const existingIds = new Set(s.messages.map(m => m.id));
          // Also filter out user/widget messages - they're handled by sendMessage
          // Only accept Slack responses through polling
          const uniqueNewMessages = newMessages.filter(m =>
            !existingIds.has(m.id) && m.source === 'slack'
          );

          if (uniqueNewMessages.length === 0) {
            return s; // No new messages to add
          }

          // We have new Slack messages - mark that we've received a reply
          // This permanently disables the no-reply fallback for this conversation
          if (noReplyTimerRef.current) {
            window.clearTimeout(noReplyTimerRef.current);
            noReplyTimerRef.current = null;
          }
          lastUserMessageTimeRef.current = null;

          return {
            ...s,
            messages: [...s.messages, ...uniqueNewMessages],
            // Hide fallback and mark that we've received a Slack reply
            showNoReplyFallback: false,
            hasReceivedSlackReply: true,
          };
        });
      }
    }, 2000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        window.clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [state.mode, state.status, state.conversationId]);

  return {
    ...state,
    startConversation,
    sendMessage,
    uploadFile,
    removeAttachment,
    completeConversation,
    confirmSpec,
    continueRefining,
    submitSpecEarly,
    clearError,
    reset,
    showLeaveMessageForm,
    hideLeaveMessageForm,
    pendingAttachments: pendingAttachmentsRef.current,
  };
}
